'use client';

import React from 'react';
import { Package, TrendingUp, Check, ArrowRight } from 'lucide-react';

interface Props {
  onPathA: () => void;
  onPathB: () => void;
}

export default function Slide5Decision({ onPathA, onPathB }: Props) {
  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 90% 50% at 50% 50%, rgba(0,168,255,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-4xl w-full mx-auto fade-in">
        <div className="text-center mb-12">
          <p className="text-primary text-sm font-semibold tracking-widest uppercase mb-3">
            Choose Your Path
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground leading-tight mb-5">
            How Would You Like to{' '}
            <span className="gradient-text">Experience X39?</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Path B — Product Only */}
          <button
            onClick={onPathB}
            className="glass-card-hover p-8 text-left group cursor-pointer w-full border border-white/10 rounded-2xl"
          >
            <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-400/20 flex items-center justify-center mb-5">
              <Package size={26} className="text-blue-400" />
            </div>
            <h3 className="text-foreground font-bold text-2xl mb-2">I Want the Product</h3>
            <p className="text-secondary-foreground text-sm mb-5 leading-relaxed">
              Start experiencing X39 with retail or preferred customer pricing. No commitment required.
            </p>
            <ul className="space-y-2 mb-6">
              {[
                'Retail: $149.95 per sleeve',
                'Preferred Customer subscription from $99.95/mo',
                '90-day money back guarantee',
              ].map((item) => (
                <li key={`pathb-${item}`} className="flex items-center gap-2 text-sm text-secondary-foreground">
                  <Check size={14} className="text-blue-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all">
              Show Me Product Options <ArrowRight size={16} />
            </div>
          </button>

          {/* Path A — Business */}
          <button
            onClick={onPathA}
            className="text-left cursor-pointer w-full rounded-2xl p-8 relative overflow-hidden border border-primary/30 group"
            style={{
              background: 'linear-gradient(135deg, rgba(0,102,204,0.15) 0%, rgba(0,168,255,0.08) 100%)',
              boxShadow: '0 0 40px rgba(0,168,255,0.15)',
            }}
          >
            <div
              className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-xs font-bold text-accent border border-accent/30"
              style={{ background: 'rgba(0,229,255,0.1)' }}
            >
              BEST VALUE
            </div>
            <div className="w-14 h-14 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-5">
              <TrendingUp size={26} className="text-primary" />
            </div>
            <h3 className="text-foreground font-bold text-2xl mb-2">
              Product + Business Opportunity
            </h3>
            <p className="text-secondary-foreground text-sm mb-5 leading-relaxed">
              Get below-wholesale pricing AND the ability to earn income sharing a product you love.
            </p>
            <ul className="space-y-2 mb-6">
              {[
                'Below-wholesale pricing on X39',
                'Earn income sharing with others',
                'Full business support & training',
                'Build residual income streams',
              ].map((item) => (
                <li key={`patha-${item}`} className="flex items-center gap-2 text-sm text-secondary-foreground">
                  <Check size={14} className="text-primary flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all">
              Show Me Everything <ArrowRight size={16} />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}